import React, { ReactNode } from 'react';
import cx from 'classnames';

import { IconX, IconWarning2 } from '..';

interface Props<I> {
    content?: ReactNode;
    kind: ToastKind;
    remove: RemoveToast;
    RenderContent?: ToastRenderContent<I>;
    renderContentInput?: I;
    position?: ToastPosition;
}

/**
 * A toast that looks like a card with a white background. Can be positioned either at the top or
 * the center of the screen.
 *
 * Only the most recently added card is shown.  If the card is set with `position: 'center'`, then
 * the rest of the screen will become unresponsive. Use this configuration when you want to force
 * the user to make a choice before proceeding.
 */
export function ToastCard<I>({
    content,
    kind,
    remove,
    RenderContent,
    renderContentInput,
    position = 'top',
}: Props<I>): JSX.Element {
    return (
        <div
            className={cx({
                'flex-grow h-screen flex flex-col justify-center':
                    position === 'center',
                'mt-8': position === 'top',
            })}
        >
            <div
                className={cx(
                    'flex justify-between bg-white rounded-lg shadow-lg p-8',
                    {
                        'w-64': typeof content === 'string',
                        'w-auto': typeof content !== 'string',
                        'border-l-4 border-quinary-500':
                            kind === 'warning-card',
                    },
                )}
            >
                {kind === 'warning-card' && (
                    <div className="flex justify-start mr-6">
                        <IconWarning2 fillColor="quinary-500" width={35} />
                    </div>
                )}
                <div className="w-auto mt-2">
                    {content && <>{content}</>}{' '}
                    {RenderContent && (
                        <RenderContent
                            remove={remove}
                            input={renderContentInput}
                        />
                    )}
                </div>
                <div className="flex justify-end ml-6">
                    <IconX
                        width={16}
                        fillColor="tertiary-400"
                        handleClick={(): void => {
                            remove({
                                canceled: true,
                                success: false,
                            });
                        }}
                    />
                </div>
            </div>
        </div>
    );
}
